/**
 * Adds several people to an event's attendee list at once.
 *
 * Built around a paste rather than a form, because the lists administrators
 * actually hold are exports — a Google Form response summary, a partner's guest
 * list, a column out of a spreadsheet. Pasting the block and correcting a couple
 * of cells is both faster and more accurate than retyping every row.
 *
 * The server settles each person idempotently (existing account reused, existing
 * registration left alone), so re-pasting a list that has grown by two names adds
 * exactly those two. That is what makes the paste safe to repeat, and it is why
 * this dialog does not try to work out who is already registered before sending.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2, UserPlus, X } from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";
import { trpc } from "@/lib/trpc";
import { crmInputStyle, crmLabelStyle, crmTextareaStyle } from "@/components/crmFormStyles";
import HighlightedTextarea, { type HighlightRange } from "@/components/HighlightedTextarea";
import { parseAttendeeRosterPaste, type ParsedAttendeeRow } from "@shared/attendeeRosterPaste";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Same shape the paste parser recognises, used here to locate them for marking. */
const EMAIL_IN_TEXT = /[^\s,;<>()[\]]+@[^\s,;<>()[\]]+\.[^\s,;<>()[\]]+/g;

const emptyRow = (): ParsedAttendeeRow => ({ fullName: "", email: "", company: "", jobTitle: "", phone: "" });

type EnrollmentResult = {
  email: string;
  fullName: string;
  accountOutcome: "created" | "existing";
  registrationOutcome: "registered" | "already_registered" | "failed";
  emailOutcome: "sent" | "queued" | "skipped" | "failed";
  error?: string;
  code?: "awaiting_review" | "reopen_required";
};

/** Plain-language account of what happened to one person. */
function describeResult(result: EnrollmentResult) {
  if (result.registrationOutcome === "failed") return result.error ?? "Could not be added.";
  if (result.registrationOutcome === "already_registered") {
    return result.accountOutcome === "created"
      ? "Account created; already on the list."
      : "Already a member and already registered — nothing changed.";
  }
  const account = result.accountOutcome === "created" ? "Free account created" : "Existing member";
  const sent = result.emailOutcome === "queued" || result.emailOutcome === "sent"
    ? ", confirmation queued"
    : result.emailOutcome === "failed"
      ? ", but the confirmation email failed"
      : "";
  return `${account}, added to the event${sent}.`;
}

export default function AddEventAttendeesDialog({
  eventSlug,
  eventTitle,
  onClose,
}: {
  eventSlug: string;
  eventTitle: string;
  onClose: () => void;
}) {
  const [pasted, setPasted] = useState("");
  const [rows, setRows] = useState<ParsedAttendeeRow[]>([emptyRow()]);
  const [problems, setProblems] = useState<Array<{ line: number; text: string; reason: "no_email" | "duplicate" }>>([]);
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(false);
  const [results, setResults] = useState<EnrollmentResult[] | null>(null);
  const [visible, setVisible] = useState(false);
  const utils = trpc.useUtils();

  /*
   * Which addresses in play already belong to members.
   *
   * Covers both the paste box and the rows below it, so an address typed
   * directly into a row is recognised the same way a pasted one is. The paste
   * text is debounced because this fires while somebody is still typing.
   */
  const [debouncedPaste, setDebouncedPaste] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPaste(pasted), 350);
    return () => clearTimeout(timer);
  }, [pasted]);

  const emailsToLookUp = useMemo(() => {
    const found = new Set<string>();
    // `exec` in a loop rather than `matchAll`: the build target predates the
    // iterator protocol on the result, and this file must compile as it stands.
    const scanner = new RegExp(EMAIL_IN_TEXT.source, "g");
    let match: RegExpExecArray | null;
    while ((match = scanner.exec(debouncedPaste)) !== null) found.add(match[0].toLowerCase());
    for (const row of rows) {
      const email = row.email.trim().toLowerCase();
      if (EMAIL_PATTERN.test(email)) found.add(email);
    }
    // The server accepts 50 per call, matching the enrollment bound.
    return Array.from(found).slice(0, 50);
  }, [debouncedPaste, rows]);

  const lookupQuery = trpc.members.lookupByEmails.useQuery(
    { emails: emailsToLookUp },
    { enabled: emailsToLookUp.length > 0, retry: false, staleTime: 30_000 },
  );

  const membersByEmail = useMemo(() => {
    const map = new Map<string, { fullName: string; company: string; jobTitle: string }>();
    for (const member of lookupQuery.data?.members ?? []) {
      map.set(member.emailNormalized.toLowerCase(), member);
    }
    return map;
  }, [lookupQuery.data]);

  /** Where each known member's address sits in the paste, so it can be underlined. */
  const memberHighlights = useMemo<HighlightRange[]>(() => {
    if (membersByEmail.size === 0) return [];
    const ranges: HighlightRange[] = [];
    const scanner = new RegExp(EMAIL_IN_TEXT.source, "g");
    let match: RegExpExecArray | null;
    while ((match = scanner.exec(pasted)) !== null) {
      if (!membersByEmail.has(match[0].toLowerCase())) continue;
      ranges.push({ start: match.index, end: match.index + match[0].length });
    }
    return ranges;
  }, [pasted, membersByEmail]);

  /** A row whose address is a member: its details come from the account, not the paste. */
  const memberFor = (email: string) => membersByEmail.get(email.trim().toLowerCase());

  const enroll = trpc.events.enrollMembers.useMutation({
    onSuccess: async response => {
      setResults(response.results);
      await utils.events.attendeeRoster.invalidate({ eventSlug });
      await utils.events.adminRegistrationCounts.invalidate();
      await utils.members.adminRecords.invalidate();

      const added = response.results.filter(result => result.registrationOutcome === "registered").length;
      const failed = response.results.filter(result => result.registrationOutcome === "failed").length;
      if (failed > 0) {
        chiNotify.error(`${failed} could not be added`, { description: "See the outcome beside each person." });
      } else {
        chiNotify.success(`${added} ${added === 1 ? "person" : "people"} added`, { description: eventTitle });
      }
    },
    onError: error => chiNotify.error("Nobody was added", { description: error.message }),
  });

  useEffect(() => {
    setVisible(true);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !enroll.isPending) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enroll.isPending, onClose]);

  const applyPaste = () => {
    const parsed = parseAttendeeRosterPaste(pasted);
    setProblems(parsed.problems);
    if (parsed.rows.length === 0) {
      chiNotify.error("No email addresses found", { description: "Each line needs an email address." });
      return;
    }
    /*
     * An existing member's company and title come from their account, not from
     * the pasted line. A registration form filled in months ago routinely
     * carries a job the person has since left, and writing that into the form
     * would show an administrator a value that contradicts the member record.
     */
    const fromAccounts = parsed.rows.map(row => {
      const member = memberFor(row.email);
      return member
        ? { ...row, fullName: member.fullName || row.fullName, company: member.company, jobTitle: member.jobTitle }
        : row;
    });

    // Anything already typed into a blank starter row is not worth preserving,
    // but a row the administrator actually filled in is.
    setRows(previous => [...previous.filter(row => row.email.trim()), ...fromAccounts]);
    setPasted("");
    setResults(null);
  };

  const updateRow = (index: number, key: keyof ParsedAttendeeRow, value: string) =>
    setRows(previous => previous.map((row, position) => (position === index ? { ...row, [key]: value } : row)));

  const removeRow = (index: number) =>
    setRows(previous => (previous.length === 1 ? [emptyRow()] : previous.filter((_, position) => position !== index)));

  const readyRows = useMemo(
    () => rows.filter(row => EMAIL_PATTERN.test(row.email.trim()) && row.fullName.trim().length > 0),
    [rows],
  );
  const incompleteCount = rows.filter(row => row.email.trim() || row.fullName.trim()).length - readyRows.length;
  const overLimit = readyRows.length > 50;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (enroll.isPending || readyRows.length === 0 || overLimit) return;
    setResults(null);
    enroll.mutate({
      eventSlug,
      sendConfirmationEmail,
      people: readyRows.map(row => ({
        fullName: row.fullName.trim(),
        email: row.email.trim(),
        company: row.company.trim() || undefined,
        jobTitle: row.jobTitle.trim() || undefined,
        phone: row.phone.trim() || undefined,
      })),
    });
  };

  return (
    <>
      <div
        role="presentation"
        onClick={() => { if (!enroll.isPending) onClose(); }}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
          zIndex: 999, opacity: visible ? 1 : 0, transition: "opacity 0.3s ease",
        }}
      />

      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-event-attendees-title"
        aria-busy={enroll.isPending}
        onSubmit={submit}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(780px, 100vw)", background: "#0D1220", zIndex: 1000,
          borderLeft: "1px solid rgba(233,30,140,.28)",
          boxShadow: "-24px 0 60px rgba(0,0,0,.5)",
          display: "flex", flexDirection: "column",
          transform: visible ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s cubic-bezier(.23,1,.32,1)",
        }}
      >
        <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", padding: "1.25rem 1.5rem", borderBottom: "1px solid rgba(255,255,255,.07)" }}>
          <div>
            <h2 id="add-event-attendees-title" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.4rem", color: "white", letterSpacing: ".04em", display: "flex", alignItems: "center", gap: ".5rem" }}>
              <UserPlus size={17} style={{ color: "#E91E8C" }} /> Add attendees
            </h2>
            <p style={{ margin: ".25rem 0 0", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>{eventTitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={enroll.isPending}
            aria-label="Close"
            style={{ background: "none", border: "none", color: "#64748B", cursor: enroll.isPending ? "not-allowed" : "pointer", padding: 0 }}
          >
            <X size={18} />
          </button>
        </header>

        <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem 1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <section>
            <label htmlFor="attendee-paste" style={crmLabelStyle}>Paste a list</label>
            <HighlightedTextarea
              id="attendee-paste"
              value={pasted}
              onChange={setPasted}
              placeholder={"Paste one person per line — name, email, company, title, phone.\nA spreadsheet or Google Form export can be pasted straight in."}
              highlights={memberHighlights}
              style={crmTextareaStyle}
              // Underlined, not coloured: the paste box is plain text and a
              // colour would read as an error rather than as recognition.
              highlightStyle={{ textDecoration: "underline", textDecorationColor: "#86EFAC", textDecorationThickness: "2px", textUnderlineOffset: "3px" }}
              ariaDescribedBy="attendee-paste-hint"
            />
            <p id="attendee-paste-hint" style={{ margin: ".4rem 0 0", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
              {memberHighlights.length > 0
                ? `${memberHighlights.length} ${memberHighlights.length === 1 ? "address is" : "addresses are"} underlined — already a member. Their company and title come from their account.`
                : "An address that already belongs to a member is underlined as you type."}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginTop: ".5rem" }}>
              <button
                type="button"
                onClick={applyPaste}
                disabled={!pasted.trim()}
                style={{ padding: ".45rem .9rem", borderRadius: ".5rem", border: "1px solid rgba(0,212,255,.3)", background: "rgba(0,212,255,.1)", color: "#00D4FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: pasted.trim() ? "pointer" : "not-allowed", opacity: pasted.trim() ? 1 : .5 }}
              >
                Read the list
              </button>
              <span style={{ color: "#475569", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                Every row stays editable below before anything is saved.
              </span>
            </div>

            {problems.length > 0 && (
              <ul style={{ margin: ".75rem 0 0", padding: ".6rem .9rem", listStyle: "none", background: "rgba(245,166,35,.08)", border: "1px solid rgba(245,166,35,.24)", borderRadius: ".5rem" }}>
                {problems.map(problem => (
                  <li key={`${problem.line}-${problem.reason}`} style={{ color: "#F5A623", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                    Line {problem.line} skipped — {problem.reason === "duplicate" ? "the same email appears earlier" : "no email address"}: {problem.text.slice(0, 80)}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: ".5rem" }}>
              <span style={crmLabelStyle}>Members to add</span>
              <span style={{ color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                {readyRows.length} ready{incompleteCount > 0 ? ` · ${incompleteCount} needs a name and email` : ""}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
              {rows.map((row, index) => {
                const result = results?.find(candidate => candidate.email.toLowerCase() === row.email.trim().toLowerCase());
                /*
                 * An existing member's details are shown, not edited. Their
                 * account is the record; typing a different company into this
                 * form would either be discarded on save — the enrollment never
                 * overwrites an existing member — or, worse, imply it was kept.
                 * The row can still be removed if it was added in error.
                 */
                const member = memberFor(row.email);
                const locked = Boolean(member);
                const lockedStyle = locked
                  ? { ...crmInputStyle, background: "rgba(134,239,172,.07)", borderColor: "rgba(134,239,172,.28)", color: "#CBD5E1", cursor: "not-allowed" }
                  : crmInputStyle;
                return (
                  <div key={index} style={{ display: "grid", gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1.4fr) minmax(0,1fr) minmax(0,1fr) 32px", gap: ".4rem", alignItems: "center" }}>
                    <input aria-label={`Name, row ${index + 1}`} value={row.fullName} onChange={event => updateRow(index, "fullName", event.target.value)} placeholder="Full name" style={lockedStyle} readOnly={locked} title={locked ? "From their member account" : undefined} />
                    <input aria-label={`Email, row ${index + 1}`} value={row.email} onChange={event => updateRow(index, "email", event.target.value)} placeholder="Email address" style={lockedStyle} readOnly={locked} title={locked ? "An existing member — details come from their account" : undefined} />
                    <input aria-label={`Company, row ${index + 1}`} value={row.company} onChange={event => updateRow(index, "company", event.target.value)} placeholder="Company" style={lockedStyle} readOnly={locked} title={locked ? "From their member account" : undefined} />
                    <input aria-label={`Title, row ${index + 1}`} value={row.jobTitle} onChange={event => updateRow(index, "jobTitle", event.target.value)} placeholder="Job title" style={lockedStyle} readOnly={locked} title={locked ? "From their member account" : undefined} />
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      aria-label={`Remove row ${index + 1}`}
                      style={{ width: 30, height: 30, borderRadius: ".4rem", border: "1px solid rgba(239,68,68,.22)", background: "rgba(239,68,68,.08)", color: "#F87171", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    >
                      <Trash2 size={13} />
                    </button>
                    {locked && !result && (
                      <p style={{ gridColumn: "1 / -1", margin: "0 0 .35rem", color: "#86EFAC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                        Existing member — details from their account, and nothing here will overwrite them.
                      </p>
                    )}
                    {result && (
                      <p style={{
                        gridColumn: "1 / -1",
                        margin: "0 0 .35rem",
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "var(--chi-body-compact)",
                        color: result.registrationOutcome === "failed" ? "#F87171" : result.registrationOutcome === "registered" ? "#86EFAC" : "#94A3B8",
                      }}>
                        {describeResult(result)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setRows(previous => [...previous, emptyRow()])}
              style={{ marginTop: ".6rem", display: "inline-flex", alignItems: "center", gap: ".35rem", padding: ".4rem .75rem", borderRadius: ".5rem", border: "1px dashed rgba(255,255,255,.16)", background: "transparent", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", cursor: "pointer" }}
            >
              <Plus size={13} /> Add a row
            </button>
          </section>

          <section style={{ padding: ".85rem 1rem", borderRadius: ".6rem", border: "1px solid rgba(255,255,255,.08)", background: "rgba(255,255,255,.02)" }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: ".6rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={sendConfirmationEmail}
                onChange={event => setSendConfirmationEmail(event.target.checked)}
                style={{ marginTop: ".2rem" }}
              />
              <span>
                <span style={{ display: "block", color: "#E2E8F0", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                  Send each person the registration confirmation
                </span>
                <span style={{ display: "block", marginTop: ".2rem", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
                  The same email self-service registration sends, with the join link and calendar invite. Anyone already registered is never emailed again. Leave this off to add people quietly.
                </span>
              </span>
            </label>
          </section>

          <p style={{ margin: 0, color: "#475569", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
            Anyone without an account gets a free member account. Existing members keep every detail they already have — nothing here overwrites them — and anyone already on this event's list is left alone. New accounts are not listed in the member directory until the member chooses to be.
          </p>
        </div>

        <footer style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", padding: "1rem 1.5rem", borderTop: "1px solid rgba(255,255,255,.07)" }}>
          <span style={{ color: overLimit ? "#F87171" : "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            {overLimit ? "Add up to 50 people at a time." : `${readyRows.length} ready to add`}
          </span>
          <div style={{ display: "flex", gap: ".6rem" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={enroll.isPending}
              style={{ padding: ".6rem 1rem", borderRadius: ".5rem", border: "1px solid rgba(255,255,255,.12)", background: "transparent", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, cursor: "pointer" }}
            >
              {results ? "Done" : "Cancel"}
            </button>
            <button
              type="submit"
              disabled={enroll.isPending || readyRows.length === 0 || overLimit}
              style={{
                display: "flex", alignItems: "center", gap: ".4rem", padding: ".6rem 1.1rem", borderRadius: ".5rem", border: "none",
                background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "white",
                fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700,
                cursor: enroll.isPending || readyRows.length === 0 || overLimit ? "not-allowed" : "pointer",
                opacity: enroll.isPending || readyRows.length === 0 || overLimit ? .55 : 1,
              }}
            >
              {enroll.isPending ? <Loader2 className="animate-spin" size={13} /> : <UserPlus size={13} />}
              {enroll.isPending ? "Adding…" : `Add ${readyRows.length || ""}`.trim()}
            </button>
          </div>
        </footer>
      </form>
    </>
  );
}
